import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Building2, ArrowLeft, FileText, CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, ChevronUp, Search, MessageSquare, Send, Download, Edit, Receipt, CornerDownLeft, CornerDownRight, CircleCheck } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { prestacoesApi, documentosApi, filesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { PrestacaoContas, DocumentoFiscal, Comentario, DocumentoFiscalStatus } from '../types';
import { formatCurrencyWithSymbol } from '../helpers/format-money';
import { useToast } from '../hooks/useToast';

const statusOptions: Array<{label: string, value: DocumentoFiscalStatus}> = [
  {
    label: "Pendentes",
    value: 'pendente'
  },
  {
    label: "Deferidos",
    value: 'deferido'
  },
  {
    label: "Indeferidos",
    value: 'indeferido'
  }
]

const getStatusBorderClass = (status: 'pendente' | 'deferido' | 'indeferido') => {
  switch (status) {
    case 'pendente':
      return 'border-yellow-400 dark:border-yellow-500';
    case 'deferido':
      return 'border-green-500 dark:border-green-500';
    case 'indeferido':
      return 'border-red-500 dark:border-red-500';
    default:
      return 'border-gray-200 dark:border-gray-700';
  }
};

const getDocumentButtonClass = (status: 'pendente' | 'deferido' | 'indeferido') => {
   switch (status) {
    case 'pendente':
      return 'bg-yellow-600 text-white disabled:bg-transparent disabled:hover:bg-yellow-100 disabled:border disabled:border-yellow-600 disabled:text-yellow-600 disabled:opacity-60'
    case 'deferido':
      return 'bg-green-600 text-white disabled:bg-transparent disabled:hover:bg-green-100 disabled:border disabled:border-green-600 disabled:text-green-600 disabled:opacity-60'
    case 'indeferido':
      return 'bg-red-600 text-white disabled:bg-transparent disabled:hover:bg-red-100 disabled:border disabled:border-red-600 disabled:text-red-600 disabled:opacity-60'
    default:
      return 'bg-gray-600 text-white disabled:bg-transparent disabled:hover:bg-gray-100 disabled:border disabled:border-gray-600 disabled:text-gray-600 disabled:opacity-60';
  }
}

const handleDownloadComprovante = async (comprovantePath: string) => {
  try {
    const url = await filesApi.getDownloadUrl(comprovantePath);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error downloading comprovante:', error);
    alert('Erro ao baixar comprovante');
  }
};

export function PrestacaoDetalhePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isApprover, isOperator, isAdminRole } = useAuthStore();
  const [prestacao, setPrestacao] = useState<PrestacaoContas | null>(null);
  const [documentos, setDocumentos] = useState<DocumentoFiscal[]>([]);
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [loading, setLoading] = useState(true);
  const [validandoDoc, setValidandoDoc] = useState<number | null>(null);
  const [documentosSelecionados, setDocumentosSelecionados] = useState<number[]>([]);
  const [validandoLote, setValidandoLote] = useState(false);
  const [motivoExpandido, setMotivoExpandido] = useState<number | null>(null);
  const [comentarioExpandido, setComentarioExpandido] = useState<number | null>(null);
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<DocumentoFiscalStatus | null>(null);
  const [novoComentario, setNovoComentario] = useState<{[key: number]: string}>({});
  const [enviandoComentario, setEnviandoComentario] = useState<number | null>(null);
  const [novoComentarioGeral, setNovoComentarioGeral] = useState('');
  const [enviandoComentarioGeral, setEnviandoComentarioGeral] = useState(false);
  const [mostrarComentarios, setMostrarComentarios] = useState(false);
  const [modalIndeferimento, setModalIndeferimento] = useState<{docId: number} | null>(null);
  const [motivoIndeferimento, setMotivoIndeferimento] = useState('');
  const [valorGlosa, setValorGlosa] = useState('');
  const [notificacaoMensagem, setNotificacaoMensagem] = useState('');
  const [modalRespostaAnaliseAberto, setModalRespostaAnaliseAberto] = useState(false);
  const [respostaAnaliseAprovada, setRespostaAnaliseAprovada] = useState(true);
  const [respostaAnaliseJustificativa, setRespostaAnaliseJustificativa] = useState('');
  const [enviandoRespostaAnalise, setEnviandoRespostaAnalise] = useState(false);
  const toast = useToast()
  const podeValidarEmLote = isApprover() || isAdminRole();

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  useEffect(() => {
    setDocumentosSelecionados(prev =>
      prev.filter(docId => {
        const doc = documentos.find(d => d.id === docId);
        return doc && doc.validacao_status.toLowerCase() === 'pendente';
      })
    );
  }, [documentos]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prestacaoData, documentosData, comentariosData] = await Promise.all([
        prestacoesApi.get(Number(id)),
        prestacoesApi.getDocumentos(Number(id)),
        prestacoesApi.getComentarios(Number(id)),
      ]);
      setPrestacao(prestacaoData);
      setDocumentos(documentosData);
      setComentarios(comentariosData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComentarioGeral = async () => {
    const comentarioTexto = novoComentarioGeral.trim();
    if (!comentarioTexto || !user || !id) return;

    try {
      setEnviandoComentarioGeral(true);
      await prestacoesApi.addComentario(Number(id), {
        comentario: comentarioTexto,
      });
      setNovoComentarioGeral('');
      await loadData();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erro ao adicionar comentário');
    } finally {
      setEnviandoComentarioGeral(false);
    }
  };

  const handleAddComentario = async (documentoId: number) => {
    const comentarioTexto = novoComentario[documentoId]?.trim();
    if (!comentarioTexto || !user) return;

    try {
      setEnviandoComentario(documentoId);
      await prestacoesApi.addComentario(Number(id), {
        comentario: comentarioTexto,
        documento_id: documentoId,
      });
      setNovoComentario(prev => ({ ...prev, [documentoId]: '' }));
      await loadData();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erro ao adicionar comentário');
    } finally {
      setEnviandoComentario(null);
    }
  };

  const handleSelecionarDocumento = (documentoId: number) => {
    const doc = documentos.find(d => d.id === documentoId);
    if (!doc || doc.validacao_status.toLowerCase() !== 'pendente') {
      return;
    }

    setDocumentosSelecionados(prev =>
      prev.includes(documentoId)
        ? prev.filter(idItem => idItem !== documentoId)
        : [...prev, documentoId]
    );
  };

  const handleDeferirSelecionados = async () => {
    if (!documentosSelecionados.length) {
      return;
    }

    try {
      setValidandoLote(true);
      await Promise.all(documentosSelecionados.map(docId => documentosApi.validar(docId, true)));
      toast.success('Documentos deferidos com sucesso');
      setDocumentosSelecionados([]);
      await loadData();
    } catch (error) {
      console.error('Erro ao validar documentos em lote:', error);
      toast.error('Erro ao deferir documentos selecionados');
    } finally {
      setValidandoLote(false);
    }
  };

  const podeSelecionarDocumento = (documento: DocumentoFiscal) =>
    podeValidarEmLote && documento.validacao_status.toLowerCase() === 'pendente';

  const handleValidarDocumento = async (documentoId: number, status: 'deferido' | 'indeferido', motivo?: string, glosa?: number, notificacao?: string) => {
    try {
      setValidandoDoc(documentoId);
      await documentosApi.validar(documentoId, status === 'deferido', motivo, glosa, notificacao);
      await loadData();
    } catch (error) {
      console.error('Error validating document:', error);
      alert('Erro ao validar documento');
    } finally {
      setValidandoDoc(null);
    }
  };

  const handleIndeferirClick = (docId: number) => {
    setModalIndeferimento({ docId });
    setMotivoIndeferimento('');
    setValorGlosa('');
    setNotificacaoMensagem('');
  };

  const handleConfirmarIndeferimento = async () => {
    if (!modalIndeferimento || !motivoIndeferimento.trim()) {
      alert('O motivo do indeferimento é obrigatório');
      return;
    }

    if (!notificacaoMensagem.trim()) {
      alert('A notificação para o administrador é obrigatória');
      return;
    }

    const glosa = valorGlosa ? parseFloat(valorGlosa) : undefined;
    await handleValidarDocumento(
      modalIndeferimento.docId,
      'indeferido',
      motivoIndeferimento,
      glosa,
      notificacaoMensagem
    );
    setModalIndeferimento(null);
  };

  const handleRequestAnalysis = async () => {
    try {
      await prestacoesApi.solicitarAprovacao(Number(id))
      toast.success('Solicitação de aprovação enviada com sucesso');
      loadData();

    } catch (e) {
      toast.error('Erro ao enviar solicitação de aprovação');
    }
  }

  const handleReplyAnalysis = () => {
    setRespostaAnaliseAprovada(true);
    setRespostaAnaliseJustificativa('');
    setModalRespostaAnaliseAberto(true);    
  }

  const handleConfirmarRespostaAnalise = async () => {
    const justificativa = respostaAnaliseJustificativa.trim();
    if (!id || !justificativa) {
      alert('A justificativa é obrigatória');
      return;
    }

    try {
      setEnviandoRespostaAnalise(true);
      await prestacoesApi.responderAprovacao(
        Number(id),
        respostaAnaliseAprovada,
        justificativa
      );
      setModalRespostaAnaliseAberto(false);
      setRespostaAnaliseJustificativa('');
      toast.success('Análise concluída com sucesso');
      await loadData();
    } catch (error) {
      console.error('Erro ao responder análise:', error);
      toast.error('Erro ao enviar resposta da análise');
    } finally {
      setEnviandoRespostaAnalise(false);
    }
  }

  const handleDownloadNF = async (documentoId: number, nfPath: string) => {
    try {
      const url = await filesApi.getDownloadUrl(nfPath);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading NF:', error);
      alert('Erro ao baixar nota fiscal');
    }
  };

  const handleEditDocument = async (documentoId: number) => {
    // `/prestacoes/${id}/documentos/${doc.id}/editar`
  }

  const getMesNome = (mes: number) => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return meses[mes - 1] || '';
  };

  const getStatusBadgeStyle = (status: 'pendente' | 'deferido' | 'indeferido') => {
    switch(status)
    {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'deferido':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'indeferido':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
    }
  }

const getStatusBadge = (status: 'pendente' | 'deferido' | 'indeferido') => {
  const icons = {
      pendente: Clock,
      deferido: CheckCircle,
      indeferido: XCircle,
      default: Clock,
    };
  
    const Icon = icons[status] || icons['default'];
    const style = getStatusBadgeStyle(status);
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${style}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

type PrestacaoAprovacaoStatus = NonNullable<PrestacaoContas['status_aprovacao']> | 'desconhecido';

const prestacaoAprovacaoConfig: Record<PrestacaoAprovacaoStatus, { label: string; classes: string; Icon: typeof Clock }> = {
  submetida: {
    label: 'Em análise',
    classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    Icon: Clock,
  },
  deferido: {
    label: 'Análise aprovada',
    classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    Icon: CheckCircle,
  },
  indeferido: {
    label: 'Análise indeferida',
    classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
    Icon: XCircle,
  },
  nao_submetida: {
    label: 'Não submetida',
    classes: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200',
    Icon: AlertCircle,
  },
  desconhecido: {
    label: 'Status desconhecido',
    classes: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200',
    Icon: AlertCircle,
  },
};

const getPrestacaoAprovacaoBadge = (status?: PrestacaoContas['status_aprovacao']) => {
  const normalized = (status ?? 'nao_submetida') as PrestacaoAprovacaoStatus;
  const config = prestacaoAprovacaoConfig[normalized] || prestacaoAprovacaoConfig.desconhecido;
  const Icon = config.Icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.classes}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="capitalize">{config.label}</span>
    </span>
  );
};

  const getTipoDocumentoBadgeStyle = (tipo: 'nota_fiscal' | 'comprovante_pagamento') => {
    switch(tipo)
    {
      case 'nota_fiscal':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'comprovante_pagamento':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    }
  }

  const getTipoLancamentoLabel = (tipo: 'rateio' | 'contas') => {
    switch(tipo)
    {
      case 'rateio':
        return 'Rateio'
      case 'contas':
        return 'Contas a pagar'
      default:
        return 'Desconhecido'
    }
  }

  const getTipoDocumentoLabel = (tipo: 'nota_fiscal' | 'comprovante_pagamento') => {
    switch(tipo)
    {
      case 'nota_fiscal':
        return 'Nota Fiscal'
      case 'comprovante_pagamento':
        return 'Comprovante'
      default:
        return 'Desconhecido'
    }
  }

  const getTipoDocumentoBadge = (tipo: 'nota_fiscal' | 'comprovante_pagamento') => {
    const label = getTipoDocumentoLabel(tipo)
    const badgeStyle = getTipoDocumentoBadgeStyle(tipo);
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeStyle}`}>
        {label}
      </span>
    );
  };

  const documentosFiltrados = documentos.filter(doc => {
    if (!filtroBusca && !filtroStatus) return true;

    const statusMatch = !filtroStatus
      || doc.validacao_status.toLowerCase() == filtroStatus

    const textMatch = (
      doc.fornecedor_nome.toLowerCase().includes(filtroBusca) ||
      doc.fornecedor_documento.toLowerCase().includes(filtroBusca) ||
      doc.numero_documento.toLowerCase().includes(filtroBusca) ||
      doc.rubrica_documento.toLowerCase().includes(filtroBusca.toLowerCase())
    )

    return statusMatch && textMatch;
  });

  const comentariosGerais = comentarios.filter(c => !c.documento_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!prestacao) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Prestação não encontrada
            </h3>
            <div className="mt-6">
              <Link
                to="/prestacoes"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Voltar para Prestações
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const documentosSelecionadosCount = documentosSelecionados.length;
  const somaDeferidos = Number(prestacao?.soma_valor_deferidos ?? 0);
  const somaPendentes = Number(prestacao?.soma_valor_pendentes ?? 0);
  const somaIndeferidos = Number(prestacao?.soma_valor_indeferidos ?? 0);
  const totalValorDocumentos = somaDeferidos + somaPendentes + somaIndeferidos;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/prestacoes"
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar para Prestações</span>
        </Link>

        {/* Header da Prestação */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-start justify-between mb-4 gap-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Prestação de Contas - {getMesNome(prestacao.mes)}/{prestacao.ano}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4" />
                  <span>{prestacao.unidade.nome}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(prestacao.criado_em).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 text-right">
              <div className="flex flex-col items-end gap-1">
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  prestacao.status === 'aberto'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {prestacao.status === 'aberto' ? 'Aberto' : 'Fechado'}
                </span>
                <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Status da prestação
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                {getPrestacaoAprovacaoBadge(prestacao.status_aprovacao)}
                <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Status de aprovação
                </span>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {prestacao.total_documentos || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total de Documentos</div>
              <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {formatCurrencyWithSymbol(totalValorDocumentos)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {prestacao.documentos_deferidos || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Deferidos</div>
              <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {formatCurrencyWithSymbol(somaDeferidos)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {prestacao.documentos_pendentes || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Pendentes</div>
              <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {formatCurrencyWithSymbol(somaPendentes)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {prestacao.documentos_indeferidos || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Indeferidos</div>
              <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                {formatCurrencyWithSymbol(somaIndeferidos)}
              </div>
            </div>
          </div>
        </div>

        {/* Sistema de Comentários Gerais */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <button
            onClick={() => setMostrarComentarios(!mostrarComentarios)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-3">
              <MessageSquare className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Comentários e Notificações
              </h2>
              {comentariosGerais.length > 0 && (
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full">
                  {comentariosGerais.length}
                </span>
              )}
            </div>
            {mostrarComentarios ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {mostrarComentarios && (
            <div className="mt-4 space-y-4">
              {/* Lista de comentários gerais */}
              {comentariosGerais.length > 0 ? (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {comentariosGerais.map((comentario) => (
                    <div
                      key={comentario.id}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {comentario.criado_por?.nome || ''}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(comentario.criado_em).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {comentario.mensagem}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Nenhum comentário ainda
                </p>
              )}

              {/* Formulário para novo comentário */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={novoComentarioGeral}
                    onChange={(e) => setNovoComentarioGeral(e.target.value)}
                    placeholder="Adicionar comentário ou notificação..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !enviandoComentarioGeral) {
                        handleAddComentarioGeral();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddComentarioGeral}
                    disabled={!novoComentarioGeral.trim() || enviandoComentarioGeral}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Enviar</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate(`/prestacoes/${id}/documentos/novo`)}
            className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:bg-primary-400 disabled:cursor-not-allowed"
            disabled={prestacao.status !== 'aberto'}
          >
            <FileText className="w-5 h-5" />
            <span>Adicionar Documento</span>
          </button>
        </div>

        {/* Busca e Ações */}
        <div className="flex items-center justify-between mb-6">
          <div className='flex items-center gap-2'>

            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                placeholder="Buscar por empresa, CNPJ ou rúbrica..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={filtroStatus as any}
                onChange={(e) => setFiltroStatus(e.target.value as DocumentoFiscalStatus)}
                className="w-full min-h-[42px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={""}>Todos status</option>
                {statusOptions.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>


          <div className="flex flex-wrap gap-2 justify-end">
            {podeValidarEmLote && (
              <button
                onClick={handleDeferirSelecionados}
                disabled={!documentosSelecionadosCount || validandoLote}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
              >
                <CircleCheck className="w-5 h-5" />
                <span>{validandoLote ? 'Deferindo...' : 'Deferir selecionados'}</span>
                {documentosSelecionadosCount > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-white/20 px-1 text-xs font-semibold">
                    {documentosSelecionadosCount}
                  </span>
                )}
              </button>
            )}
            {(isAdminRole() || isOperator()) && (
              <button
                onClick={handleRequestAnalysis}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                disabled={prestacao.status !== 'aberto'}
              >
                <CornerDownRight className="w-5 h-5" />
                <span>Enviar para análise</span>
              </button>
            )}

            {(isApprover() || isAdminRole()) && (
              <button
                onClick={handleReplyAnalysis}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                disabled={prestacao.status_aprovacao !== 'submetida'}
              >
                <CircleCheck className="w-5 h-5" />
                <span>Finalizar análise</span>
              </button>
              )}
          </div>
        </div>

        {/* Lista de Documentos */}
        <div className="space-y-4">
          {documentosFiltrados.map((doc) => {
            const comentariosDoc = comentarios.filter(c => c.documento_id === doc.id);
            const comentarioAberto = comentarioExpandido === doc.id;

            const buttonStyle = getDocumentButtonClass(doc.validacao_status.toLowerCase() as any)

            // CARD DOCUMENTO
            return (
              <div
                key={doc.id}
                id={`documento_${doc.id}`}
                className={[
                  'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6',
                  getStatusBorderClass(doc.validacao_status.toLowerCase() as any),
                ].join(' ')}
              >
                <div className="flex items-start justify-between mb-4 gap-4">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {doc.fornecedor_nome}
                      </h3>
                      {/*getTipoDocumentoBadge(doc.tipo_documento)*/}
                      {getStatusBadge(doc.validacao_status.toLowerCase() as any)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">CNPJ:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-mono">
                          {doc.fornecedor_documento || ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Nº Documento:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {doc.numero_documento || ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Data Emissão:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {new Date(doc.data_emissao).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Rubrica:</span>
                        <span className="ml-2 text-primary-600 dark:text-primary-400 font-medium">
                          {doc.rubrica_documento}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Valor Original:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-semibold">
                          {formatCurrencyWithSymbol(doc.valor_original)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Valor Líquido:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-semibold">
                          {formatCurrencyWithSymbol(doc.valor_liquido)}
                        </span>
                      </div>

                      {doc.valor_glosa && parseFloat(doc.valor_glosa) > 0 && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Glosa:</span>
                          <span className="ml-2 text-red-600 dark:text-red-400 font-semibold">
                            {formatCurrencyWithSymbol(doc.valor_glosa)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                
                  {/* ✅ Ações do Documento: Ver NF / Ver Comprovante / Editar */}
                  <div className="flex flex-col items-end gap-2 min-w-[180px]">
                    {podeSelecionarDocumento(doc) && (
                      <label className="inline-flex items-center text-sm text-gray-600 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={documentosSelecionados.includes(doc.id)}
                          onChange={() => handleSelecionarDocumento(doc.id)}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2">Selecionar</span>
                      </label>
                    )}
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        onClick={() => handleDownloadNF(doc.id, doc.caminho_arquivo_nota_fiscal!)}
                        disabled={!doc.caminho_arquivo_nota_fiscal}
                        className={["inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors", buttonStyle].join(' ')}
                      >
                        <FileText className="w-4 h-4" />
                        <span>Ver Nota Fiscal</span>
                        <span className={`ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full ${!doc.caminho_arquivo_nota_fiscal ? 'bg-black/5' : 'bg-white/20' } px-1 text-xs font-semibold`}>
                          {!!doc.caminho_arquivo_nota_fiscal ? 1 : 0}
                        </span>
                      </button>
    
                      <button
                        onClick={() => handleDownloadComprovante(doc.caminho_arquivo_comprovante || '')}
                        disabled={!doc.caminho_arquivo_comprovante}
                        className={["inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors", buttonStyle].join(' ')}
                      >
                        <Receipt className="w-4 h-4" />
                        <span>Ver Comprovante</span>
                        <span className={`ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full ${!doc.caminho_arquivo_comprovante ? 'bg-black/5' : 'bg-white/20' } px-1 text-xs font-semibold`}>
                          {!!doc.caminho_arquivo_comprovante ? 1 : 0}
                        </span>
                      </button>

                      {/*
                      <button
                        onClick={() => handleEditDocument(doc.id)}
                        className={["inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors", buttonStyle].join(' ')}
                      >
                        <Edit className="w-4 h-4" />
                        <span>Editar</span>
                      </button>
                      */}
                    </div>                    
                  </div>
                </div>

                {/* Motivo de Indeferimento */}
                {doc.validacao_status === 'indeferido' && doc.motivo_indeferimento && (
                  <div className="mb-4">
                    <button
                      onClick={() => setMotivoExpandido(motivoExpandido === doc.id ? null : doc.id)}
                      className="w-full flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-left"
                    >
                      <span className="text-sm font-medium text-red-800 dark:text-red-300">
                        Motivo do Indeferimento
                      </span>
                      {motivoExpandido === doc.id ? (
                        <ChevronUp className="w-4 h-4 text-red-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-red-600" />
                      )}
                    </button>
                    {motivoExpandido === doc.id && (
                      <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          {doc.motivo_indeferimento}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Comentários do Documento */}
                {comentariosDoc.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => setComentarioExpandido(comentarioAberto ? null : doc.id)}
                      className="w-full flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Comentários ({comentariosDoc.length})
                        </span>
                      </div>
                      {comentarioAberto ? (
                        <ChevronUp className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    {comentarioAberto && (
                      <div className="mt-2 space-y-2 max-h-40 overflow-y-auto p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                        {comentariosDoc.map((comentario) => (
                          <div
                            key={comentario.id}
                            className="bg-white dark:bg-gray-700 rounded p-2 text-sm"
                          >
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-medium text-gray-900 dark:text-white text-xs">
                                {comentario.criado_por?.nome}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(comentario.criado_em).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300">
                              {comentario.mensagem}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Adicionar Comentário */}
                <div className="flex space-x-2 mb-4">
                  <input
                    type="text"
                    value={novoComentario[doc.id] || ''}
                    onChange={(e) => setNovoComentario(prev => ({ ...prev, [doc.id]: e.target.value }))}
                    placeholder="Adicionar comentário..."
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && enviandoComentario !== doc.id) {
                        handleAddComentario(doc.id);
                      }
                    }}
                  />
                  <button
                    onClick={() => handleAddComentario(doc.id)}
                    disabled={!novoComentario[doc.id]?.trim() || enviandoComentario === doc.id}
                    className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>

                {/* Ações de Validação */}
                {doc.validacao_status.toLowerCase() === 'pendente' && (isApprover() || isAdminRole()) && (
                  <div className="mt-4 flex space-x-2 pt-4 border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleValidarDocumento(doc.id, 'deferido')}
                      disabled={validandoDoc === doc.id}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Deferir</span>
                    </button>
                    <button
                      onClick={() => handleIndeferirClick(doc.id)}
                      disabled={validandoDoc === doc.id}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Indeferir</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {documentosFiltrados.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Nenhum documento encontrado
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filtroBusca ? 'Tente ajustar sua busca' : 'Adicione o primeiro documento desta prestação'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Modal Resposta Análise */}
      {modalRespostaAnaliseAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <CornerDownLeft className="w-6 h-6 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Responder solicitação
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Escolha o resultado da análise e informe a justificativa
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Resultado
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRespostaAnaliseAprovada(true)}
                    className={[
                      'px-4 py-3 rounded-lg border flex items-center justify-center gap-2 text-sm font-medium transition-colors',
                      respostaAnaliseAprovada
                        ? 'border-green-500 bg-green-50 text-green-700 dark:border-green-400 dark:bg-green-900/20 dark:text-green-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-green-400'
                    ].join(' ')}
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Aprovar</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRespostaAnaliseAprovada(false)}
                    className={[
                      'px-4 py-3 rounded-lg border flex items-center justify-center gap-2 text-sm font-medium transition-colors',
                      !respostaAnaliseAprovada
                        ? 'border-red-500 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-900/20 dark:text-red-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-red-400'
                    ].join(' ')}
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Indeferir</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Justificativa *
                </label>
                <textarea
                  value={respostaAnaliseJustificativa}
                  onChange={(e) => setRespostaAnaliseJustificativa(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                  rows={4}
                  placeholder="Informe o motivo da aprovação ou indeferimento..."
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Esta justificativa será registrada junto à prestação.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setModalRespostaAnaliseAberto(false)}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarRespostaAnalise}
                disabled={!respostaAnaliseJustificativa.trim() || enviandoRespostaAnalise}
                className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enviandoRespostaAnalise ? 'Enviando...' : 'Enviar resposta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Indeferimento */}
      {modalIndeferimento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Indeferir Documento
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Preencha as informações obrigatórias
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo do Indeferimento *
                </label>
                <textarea
                  value={motivoIndeferimento}
                  onChange={(e) => setMotivoIndeferimento(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Descreva o motivo do indeferimento..."
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor da Glosa (opcional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={valorGlosa}
                  onChange={(e) => setValorGlosa(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notificação para o Administrador *
                </label>
                <textarea
                  value={notificacaoMensagem}
                  onChange={(e) => setNotificacaoMensagem(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Mensagem que será enviada ao administrador sobre este indeferimento..."
                  rows={3}
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Esta mensagem será recebida pelo administrador como notificação
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setModalIndeferimento(null)}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarIndeferimento}
                disabled={!motivoIndeferimento.trim() || !notificacaoMensagem.trim()}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar Indeferimento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
